import { orderBy as _orderBy } from 'lodash';

import OfferEvent from '@core/models/offer/OfferEvent';
import {
    EventCategory,
    SuperOfferKeys,
    SuperOfferOrder,
} from '@models/offer/enums';
import StoreUtil from '@store/StoreUtil';
import groupBy from '@utils/dataTransformation/groupBy';

import {
    isSuperOfferValid,
    eventsById,
    isMarketAvailable,
    isEventSuperAdvantagePageEligible,
} from './helpers';
import {
    Getters,
    ISportOfferState,
    PromotionGroupPractical,
} from './types';
import { createSimpleGetter } from '@superbet-group/web.lib.vue-utilities';

const getters: Getters = {
    sports(state) {
        return state.sports;
    },
    tournaments(state) {
        return state.tournaments;
    },
    markets(state) {
        return state.markets;
    },
    marketTree(state) {
        return state.marketTree;
    },
    categories(state) {
        return state.categories;
    },
    oddTypes(state) {
        return state.oddTypes;
    },
    eventsMap(state) {
        return state.events;
    },
    events(state) {
        return Object.values(state.events);
    },
    topTenEvents(state) {
        return eventsById(state.topTenEvents, state);
    },
    topEvents(state) {
        return eventsById(state.topEventIds, state);
    },
    superAdvantageEvents(_state, getters) {
        const events = getters.events;

        // TODO: Refactor to BE call when available
        return events.filter(isEventSuperAdvantagePageEligible);
    },
    promotionalEvents(state) {
        const eventGroups = [] as PromotionGroupPractical[];

        Object.entries(state.promotionalEvents).forEach(([id, promotion]) => {
            const events = eventsById(promotion.matchIds, state).filter(isMarketAvailable(id));

            if (!isSuperOfferValid(id, events)) {
                return;
            }

            eventGroups.push({
                id,
                nameKey: `superOffer.${id}.name`,
                primaryMarketAttribute: id,
                events,
                order: SuperOfferOrder[id as SuperOfferKeys],
                footerKey: `superOffer.${id}.footer`,
                type: id,
            });
        });

        return eventGroups;
    },
    superLiveEvents(state, getters) {
        return getters.events
            .filter((event: OfferEvent) => event.isSuperLiveEvent);
    },
    isOfferStarting(state) {
        return state.isOfferStarting;
    },
    isOfferReady(state) {
        return state.isOfferReady;
    },
    featuredItems(state) {
        return _orderBy(state.featuredTournaments, 'order');
    },
    getMarketGroupInfosForSportId(state) {
        // Markets groups are sorted in an ascending
        // order based on the `order` property
        const sortedMarketGroupInfos = Object.values(state.marketGroupInfos)
            .sort((a, b) => a.order - b.order);
        const infos = groupBy(sortedMarketGroupInfos, 'sportId');
        return (sportId: number) => infos.get(sportId) || [];
    },
    liveEventsCount: StoreUtil.createSimpleGetter('liveEventsCount'),
    isStructFetched: StoreUtil.createSimpleGetter('isStructFetched'),
    areLiveEventsFetched: createSimpleGetter('areLiveEventsFetched'),
    allChangesIncrementId: StoreUtil.createSimpleGetter('allChangesIncrementId'),
    areSubscriptionsSet: StoreUtil.createSimpleGetter('areSubscriptionsSet'),
    isEventsSubscriptionSet: StoreUtil.createSimpleGetter('isEventsSubscriptionSet'),
    isOfferFetched: StoreUtil.createSimpleGetter('isOfferFetched'),
    getIsOfferFetchedBySport: (state: ISportOfferState) =>
        (sportId: number): boolean => state.isOfferFetched || !!state.isOfferFetchedBySport[sportId],
    isSuperOfferFetched: StoreUtil.createSimpleGetter('isSuperOfferFetched'),
    areTopTenEventsFetched: StoreUtil.createSimpleGetter('areTopTenEventsFetched'),
    areTopEventIdsFetched: StoreUtil.createSimpleGetter('areTopEventIdsFetched'),
    oddValueDynamics: StoreUtil.createSimpleGetter('oddValueDynamics'),
    outrights(state, getters) {
        return getters.events
            .filter((event) => event.eventCategory === EventCategory.Outright);
    },
    getCompetitionTreeBySportId(state) {
        return (sportId: number) => state.competitionTreeBySportId[sportId] ?? [];
    },
    isFirstDailyOfferSync: createSimpleGetter<ISportOfferState, 'isFirstDailyOfferSync'>('isFirstDailyOfferSync'),
    isFirstStructSync: createSimpleGetter<ISportOfferState, 'isFirstStructSync'>('isFirstStructSync'),
    sportDatesWithActiveOffer(state) {
        return state.sportDatesWithActiveOffer;
    },
    popularSuperbets: createSimpleGetter<ISportOfferState, 'popularSuperbets'>('popularSuperbets'),
    popularSuperbetsV2: createSimpleGetter<ISportOfferState, 'popularSuperbetsV2'>('popularSuperbetsV2'),
};

export default getters;
